<section class="horizon-card">
  <header class="horizon-card__head">
    <div>
      <h3 class="horizon-card__title">Buyer Preferences</h3>
      <p class="horizon-card__sub" *ngIf="updatedText">{{ updatedText }}</p>
    </div>
    <button type="button" class="_outline-btn" *ngIf="!isLoading && !hasLoadError" (click)="openEdit()">
      <i class="pi pi-pencil mr-2" aria-hidden="true"></i>Edit
    </button>
  </header>

  <div class="horizon__state" *ngIf="isLoading && !data"><i class="pi pi-spin pi-spinner" aria-hidden="true"></i> Loading preferences…</div>
  <div class="horizon__state horizon__state--error" *ngIf="hasLoadError">
    Preferences could not be loaded.
    <button type="button" class="_outline-btn" (click)="load()">Retry</button>
  </div>

  <p class="horizon__empty" *ngIf="data && !hasPreferences">
    This buyer has not saved any preferences for this project yet. Use Edit to record what they tell you.
  </p>

  <dl class="bp" *ngIf="hasPreferences">
    <div>
      <dt>Budget</dt>
      <dd>{{ budgetText }}</dd>
    </div>
    <div>
      <dt>Property Type</dt>
      <dd>{{ typeLabel(preferences.property_type) }}</dd>
    </div>
    <div>
      <dt>Bedrooms</dt>
      <dd>{{ minimum(preferences.bedrooms) }}</dd>
    </div>
    <div>
      <dt>Bathrooms</dt>
      <dd>{{ minimum(preferences.bathrooms) }}</dd>
    </div>
    <div>
      <dt>Car Spaces</dt>
      <dd>{{ minimum(preferences.car_spaces) }}</dd>
    </div>
  </dl>

  <div class="bp-features" *ngIf="hasPreferences && preferences.features.length">
    <small>Preferred Features</small>
    <ul>
      <li *ngFor="let feature of preferences.features">{{ featureLabel(feature) }}</li>
    </ul>
  </div>

  <p class="bp-note" *ngIf="hasPreferences && preferences.additional_requirements">
    <small>Additional Requirements</small>
    {{ preferences.additional_requirements }}
  </p>

  <div class="bp-shortlist" *ngIf="shortlist.length">
    <h4 class="bp-shortlist__title">
      Shortlisted Properties <span class="horizon__muted">({{ shortlist.length }})</span>
    </h4>
    <ul>
      <li *ngFor="let item of shortlist; trackBy: trackByUnit">
        <span class="bp-shortlist__thumb"
          ><img *ngIf="item.image" [src]="item.image" alt="" loading="lazy" /><i
            *ngIf="!item.image"
            class="fa-regular fa-image"
            aria-hidden="true"></i
        ></span>
        <span class="bp-shortlist__text">
          <strong>{{ item.name }}</strong>
          <small>{{ item.bed }} bed · {{ item.bath }} bath · {{ item.car }} car</small>
        </span>
        <span class="bp-shortlist__price">{{ money(item.price) }}</span>
      </li>
    </ul>
  </div>
</section>

<p-dialog header="Edit Buyer Preferences" [(visible)]="isDialogOpen" [modal]="true" [draggable]="false" [style]="{ width: 'min(640px, 94vw)' }">
  <div class="bpd">
    <p class="bpd__intro">
      These are the buyer's preferences for this project. Changes show for the buyer in Horizon straight away, and the buyer is not notified.
    </p>

    <div class="bpd__grid">
      <div>
        <label class="bpd__label" for="pref-min">Budget From</label>
        <div class="bpd__money">
          <span class="bpd__money-sign" aria-hidden="true">$</span>
          <p-inputNumber
            inputId="pref-min"
            mode="decimal"
            locale="en-AU"
            [useGrouping]="true"
            [minFractionDigits]="0"
            [maxFractionDigits]="2"
            [min]="0"
            [style]="{ width: '100%' }"
            inputStyleClass="form-control"
            placeholder="850,000"
            [(ngModel)]="draft.budget_min"></p-inputNumber>
        </div>
      </div>
      <div>
        <label class="bpd__label" for="pref-max">Budget Up To</label>
        <div class="bpd__money">
          <span class="bpd__money-sign" aria-hidden="true">$</span>
          <p-inputNumber
            inputId="pref-max"
            mode="decimal"
            locale="en-AU"
            [useGrouping]="true"
            [minFractionDigits]="0"
            [maxFractionDigits]="2"
            [min]="0"
            [style]="{ width: '100%' }"
            inputStyleClass="form-control"
            placeholder="950,000"
            [(ngModel)]="draft.budget_max"></p-inputNumber>
        </div>
      </div>
    </div>
    <p class="bpd__error" *ngIf="fieldErrors['budget_min']">{{ fieldErrors['budget_min'] }}</p>
    <p class="bpd__error" *ngIf="fieldErrors['budget_max']">{{ fieldErrors['budget_max'] }}</p>
    <p class="bpd__hint" *ngIf="budgetHint">{{ budgetHint }}</p>
    <p class="bpd__error" *ngIf="isRangeInvalid">The minimum budget cannot be higher than the maximum.</p>

    <label class="bpd__label">Property Type</label>
    <ng-select
      [items]="propertyTypes"
      bindLabel="label"
      bindValue="key"
      placeholder="Any"
      [searchable]="false"
      appendTo="body"
      [(ngModel)]="draft.property_type"></ng-select>
    <p class="bpd__error" *ngIf="fieldErrors['property_type']">{{ fieldErrors['property_type'] }}</p>

    <div class="bpd__grid bpd__grid--three">
      <div>
        <label class="bpd__label">Bedrooms (Min)</label>
        <input
          *ngIf="bedroomsFree; else bedroomsSelect"
          type="number"
          min="0"
          [max]="freeCountMax"
          step="1"
          class="form-control"
          placeholder="Any"
          [(ngModel)]="draft.bedrooms" />
        <ng-template #bedroomsSelect>
          <ng-select
            [items]="bedroomChoices"
            bindLabel="label"
            bindValue="value"
            placeholder="Any"
            [searchable]="false"
            appendTo="body"
            [(ngModel)]="draft.bedrooms"></ng-select>
        </ng-template>
        <p class="bpd__error" *ngIf="fieldErrors['bedrooms']">{{ fieldErrors['bedrooms'] }}</p>
      </div>
      <div>
        <label class="bpd__label">Bathrooms (Min)</label>
        <input
          *ngIf="bathroomsFree; else bathroomsSelect"
          type="number"
          min="0"
          [max]="freeCountMax"
          step="1"
          class="form-control"
          placeholder="Any"
          [(ngModel)]="draft.bathrooms" />
        <ng-template #bathroomsSelect>
          <ng-select
            [items]="bathroomChoices"
            bindLabel="label"
            bindValue="value"
            placeholder="Any"
            [searchable]="false"
            appendTo="body"
            [(ngModel)]="draft.bathrooms"></ng-select>
        </ng-template>
        <p class="bpd__error" *ngIf="fieldErrors['bathrooms']">{{ fieldErrors['bathrooms'] }}</p>
      </div>
      <div>
        <label class="bpd__label">Car Spaces (Min)</label>
        <input
          *ngIf="carSpacesFree; else car_spacesSelect"
          type="number"
          min="0"
          [max]="freeCountMax"
          step="1"
          class="form-control"
          placeholder="Any"
          [(ngModel)]="draft.car_spaces" />
        <ng-template #car_spacesSelect>
          <ng-select
            [items]="carChoices"
            bindLabel="label"
            bindValue="value"
            placeholder="Any"
            [searchable]="false"
            appendTo="body"
            [(ngModel)]="draft.car_spaces"></ng-select>
        </ng-template>
        <p class="bpd__error" *ngIf="fieldErrors['car_spaces']">{{ fieldErrors['car_spaces'] }}</p>
      </div>
    </div>

    <label class="bpd__label">Preferred Features</label>
    <ng-select
      [items]="options?.features ?? []"
      bindLabel="label"
      bindValue="key"
      [multiple]="true"
      [closeOnSelect]="false"
      placeholder="Choose features"
      appendTo="body"
      [(ngModel)]="draft.features"></ng-select>
    <p class="bpd__error" *ngIf="fieldErrors['features']">{{ fieldErrors['features'] }}</p>

    <label class="bpd__label" for="pref-notes">Additional Requirements</label>
    <textarea
      id="pref-notes"
      class="form-control"
      rows="3"
      [maxlength]="requirementsLimit"
      placeholder="Anything else the buyer has asked for…"
      [(ngModel)]="draft.additional_requirements"></textarea>
    <p class="bpd__error" *ngIf="fieldErrors['additional_requirements']">{{ fieldErrors['additional_requirements'] }}</p>
    <small class="bpd__counter">{{ (draft.additional_requirements || '').length }} / {{ requirementsLimit }}</small>
  </div>
  <ng-template pTemplate="footer">
    <button type="button" class="_cancel-btn mr-2" (click)="isDialogOpen = false">Cancel</button>
    <button type="button" class="_primary-btn" [disabled]="isSaving || isRangeInvalid" (click)="save()">
      {{ isSaving ? 'Saving…' : 'Save Preferences' }}
    </button>
  </ng-template>
</p-dialog>
